import { Stack, Typography, Box, Button, Divider } from "@mui/material";
import { LoadingButton } from "@mui/lab";

/* ---------- Light theme tokens (matched to PlanSelectionStep) ---------- */
const BG = "#ffffff";
const CARD_BG = "#ffffff";
const BORDER = "rgba(0,0,0,0.75)";
const TEXT = "#111815";
const MUTED = "#5b6b62";
const GREEN = "blue";
const GREEN_STRONG = "blue";
const GREEN_TEXT = "#062617";

const TAX_RATE = 0.18; // 18%
const GATEWAY_FEE_RATE = 0.0236; // 2.36%

function formatMoney(amount, currency = "INR") {
  const symbol = currency === "INR" ? "₹" : "$";
  return `${symbol}${Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const Row = ({ label, value, bold = false, muted = false }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
    <Typography
      sx={{
        fontSize: 13.5,
        fontWeight: bold ? 700 : 400,
        color: muted ? "rgba(17,24,21,0.4)" : bold ? TEXT : MUTED,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13.5, fontWeight: bold ? 700 : 400, color: TEXT }}>
      {value}
    </Typography>
  </Box>
);

// plan here is the payload produced by PlanSelectionStep:
// { line_items: [{ label, unit_price, quantity, line_total }], total_cost, currency, duration }
//
// line_items is expected to be an array, but some callers send a single
// object instead (as seen from PlanSelectionStep). Normalize here so this
// component works either way instead of throwing on .map().
export default function ReviewStep({ account, plan, onBack, onConfirm, isSubmitting }) {
  const billingLabel = plan.duration === "Y" ? "/year" : plan.duration === "M" ? "/month" : " one-time";

  const lineItems = Array.isArray(plan.line_items)
    ? plan.line_items
    : plan.line_items
    ? [plan.line_items]
    : [];

  const acItem = lineItems.find((item) => item.label === "AC Charger");
  const dcItem = lineItems.find((item) => item.label === "DC Charger");

  const acQuantity = acItem?.quantity ?? 0;
  const dcQuantity = dcItem?.quantity ?? 0;
  const acTotal = acItem?.line_total ?? 0;
  const dcTotal = dcItem?.line_total ?? 0;

  const subtotal = acTotal + dcTotal;
  const taxAmount = subtotal * TAX_RATE;
  const subtotalAfterTax = subtotal + taxAmount;
  const gatewayFee = subtotalAfterTax * GATEWAY_FEE_RATE;
  const totalCost = subtotalAfterTax + gatewayFee;

  const costBreakdown = {
    ac_count: acQuantity,
    dc_count: dcQuantity,
    ac_charges: acItem?.unit_price ?? 0,
    dc_charges: dcItem?.unit_price ?? 0,
    subtotal,
    tax_amount: taxAmount,
    gateway_fee: gatewayFee,
    total_cost: totalCost,
  };

  return (
    <Box
      sx={{
        bgcolor: BG,
        borderRadius: "18px",
        border: `1px solid ${BORDER}`,
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography
            sx={{
              color: GREEN,
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              mb: 0.75,
            }}
          >
            Almost there
          </Typography>
          <Typography
            sx={{
              color: TEXT,
              fontWeight: 800,
              fontSize: { xs: 22, md: 26 },
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              mb: 1,
            }}
          >
            Review &amp; Pay
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 13.5, lineHeight: 1.5 }}>
            Confirm your details before you pay.
          </Typography>
        </Box>

        {account && (
          <Box
            sx={{
              bgcolor: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: "14px",
              p: { xs: 2, md: 2.25 },
            }}
          >
            <Typography sx={{ color: TEXT, fontWeight: 800, fontSize: 15, mb: 1.25 }}>
              Review Your Account
            </Typography>
            <Row label="Name" value={account.name} />
            <Row label="Email" value={account.email} />
            {account.company && <Row label="Company" value={account.company} />}
          </Box>
        )}

        <Box
          sx={{
            bgcolor: CARD_BG,
            border: `1px solid ${BORDER}`,
            borderRadius: "14px",
            p: { xs: 2, md: 2.25 },
          }}
        >
          <Typography sx={{ color: TEXT, fontWeight: 800, fontSize: 15, mb: 1.25 }}>
            Cost Summary
          </Typography>

          <Row label={`AC Chargers (${acQuantity})`} value={formatMoney(acTotal, plan.currency)} />
          <Row label={`DC Chargers (${dcQuantity})`} value={formatMoney(dcTotal, plan.currency)} />
          <Row label="Subtotal" value={formatMoney(subtotal, plan.currency)} />
          <Row label="Tax (18%)" value={formatMoney(taxAmount, plan.currency)} />
          <Row label="Subtotal after tax" value={formatMoney(subtotalAfterTax, plan.currency)} />
          <Row
            label="Payment gateway fee incl. taxes (2.36%)"
            value={formatMoney(gatewayFee, plan.currency)}
          />

          <Divider sx={{ my: 1.5, borderColor: BORDER }} />

          <Row
            label="Total annual cost"
            value={`${formatMoney(totalCost, plan.currency)}${billingLabel}`}
            bold
          />
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            onClick={onBack}
            sx={{
             
              color: "black",
              border: "1px solid rgba(10, 10, 10, 0.63)",
              borderRadius: "10px",
              px: 2.5,
              py: 0.75,
              fontWeight: 800,
              fontSize: 14,
              textTransform: "none",
              "&:hover": { bgcolor: "rgba(122,217,168,0.22)" },
            }}
          >
            Back
          </Button>
          <LoadingButton
            loading={isSubmitting}
            variant="contained"
            onClick={() => onConfirm(costBreakdown)}
            sx={{
                mt: 1.5,
                "&:hover": {
                  bgcolor: "primary.main",
                },
                "&.Mui-disabled": {
                  color: "grey",
                },
              }}
            >
            Continue to Payment
          </LoadingButton>
        </Stack>
      </Stack>
    </Box>
  );
}